import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Animated, Easing, FlatList, Image, InteractionManager, StyleSheet, Text, View } from 'react-native';
import { useReferenceWindowDimensions } from '../utils/referenceViewport';
import { SafeAreaView } from 'react-native-safe-area-context';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query';
import { Info, Play } from 'lucide-react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';
import { RootStackParamList } from '../navigation/types';
import { useSession } from '../session/SessionProvider';
import { getGenres, getItems, getItemsPage, imageSource, JellyfinItem } from '../api/jellyfin';
import { MatureTopNav, useBottomNavInset } from '../components/MatureTopNav';
import { FocusablePressable } from '../components/FocusablePressable';
import { MediaCard } from '../components/MediaCard';
import { MediaShelf } from '../components/MediaShelf';
import { colors, getBrowseVisualTokens, radius, systemSansFont } from '../theme/tokens';
import { getImageAccent } from '../utils/imageAccent';

type Props = NativeStackScreenProps<RootStackParamList, 'Library' | 'ServerLibrary'>;
type BrowseGenre = { genre: JellyfinItem; artwork?: JellyfinItem };

function formatRuntime(ticks?: number) {
    if (!ticks) return undefined;
    const minutes = Math.round(ticks / 600_000_000);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
}

function BrowseAction({ kind, accent, onPress }: { kind: 'play' | 'info'; accent: string; onPress: () => void }) {
    const primary = kind === 'play';
    return (
        <FocusablePressable
            accessibilityRole="button"
            accessibilityLabel={primary ? 'Play featured title' : 'Open featured title details'}
            onPress={onPress}
            style={(focused, pressed, hovered) => [
                styles.heroAction,
                primary ? { backgroundColor: accent, borderColor: accent } : styles.heroActionSecondary,
                (focused || hovered) && styles.heroActionActive,
                focused && styles.heroActionFocused,
                pressed && styles.pressed,
            ]}
        >
            {primary ? <Play color="#fff" fill="#fff" size={17} /> : <Info color="#fff" size={18} />}
            <Text style={styles.heroActionText}>{primary ? 'Play' : 'More Info'}</Text>
        </FocusablePressable>
    );
}

function GenreStrip({ genres, credentials, pagePadding, onPress }: { genres: BrowseGenre[]; credentials: NonNullable<ReturnType<typeof useSession>['credentials']>; pagePadding: number; onPress: (genre: JellyfinItem) => void }) {
    const { width } = useReferenceWindowDimensions();
    const browseVisuals = getBrowseVisualTokens(width);
    const compact = browseVisuals.viewport === 'compact';
    const tileWidth = compact ? Math.min(184, Math.round(width * 0.43)) : browseVisuals.viewport === 'television' ? 250 : 212;
    if (!genres.length) return null;
    return (
        <View style={styles.genreSection}>
            <Text style={[styles.sectionTitle, { marginHorizontal: pagePadding, fontSize: browseVisuals.shelfTitleSize, lineHeight: browseVisuals.shelfTitleLineHeight }]}>Browse by genre</Text>
            <FlatList
                horizontal
                data={genres}
                keyExtractor={(entry, index) => entry.genre.Id ?? entry.genre.Name ?? `genre-${index}`}
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{ paddingHorizontal: pagePadding, gap: compact ? 8 : 10 }}
                renderItem={({ item }) => (
                    <FocusablePressable
                        accessibilityLabel={`Browse ${item.genre.Name ?? 'genre'}`}
                        onPress={() => onPress(item.genre)}
                        style={(focused, pressed, hovered) => [styles.genreTile, { width: tileWidth }, (focused || hovered) && styles.genreTileActive, focused && styles.genreTileFocused, pressed && styles.pressed]}
                    >
                        {item.artwork ? <Image source={imageSource(credentials, item.artwork, item.artwork.BackdropImageTags?.length ? 'Backdrop' : 'Primary', tileWidth * 2)} resizeMode="cover" style={StyleSheet.absoluteFillObject} /> : null}
                        <View pointerEvents="none" style={styles.genreShade} />
                        <View pointerEvents="none" style={styles.genreAccent} />
                        <Text numberOfLines={1} style={[styles.genreName, browseVisuals.viewport === 'television' && styles.genreNameTelevision]}>{item.genre.Name ?? 'Genre'}</Text>
                    </FocusablePressable>
                )}
            />
        </View>
    );
}

export function LibraryLandingScreen({ navigation, route }: Props) {
    const { width } = useReferenceWindowDimensions();
    const { credentials } = useSession();
    const bottomNavInset = useBottomNavInset();
    const serverLibraryParams = route.name === 'ServerLibrary'
        ? route.params as RootStackParamList['ServerLibrary']
        : undefined;
    const itemType = serverLibraryParams?.itemType ?? route.params?.itemType ?? 'Movie';
    const noun = itemType === 'Series' ? 'TV Shows' : 'Movies';
    const libraryId = serverLibraryParams?.libraryId;
    const libraryName = serverLibraryParams?.libraryName.trim();
    const destinationName = libraryName || noun;
    const browseVisuals = getBrowseVisualTokens(width);
    const compact = browseVisuals.viewport === 'compact';
    const television = browseVisuals.viewport === 'television';
    const pagePadding = browseVisuals.pagePadding;
    const heroHeight = compact ? 390 : television ? 600 : 500;
    const [pageScrolled, setPageScrolled] = useState(false);
    const [heroAccent, setHeroAccent] = useState('rgb(166,35,47)');
    const [deferredRowsReady, setDeferredRowsReady] = useState(false);
    const heroOpacity = useRef(new Animated.Value(0)).current;
    useEffect(() => {
        // Let the route transition and first hero frame settle before starting
        // the four secondary shelves, genre lookup, and full catalog query.
        // Launching all six requests during a TV navigation event causes a
        // burst of JSON parsing and card mounting exactly while focus is moving.
        const task = InteractionManager.runAfterInteractions(() => setDeferredRowsReady(true));
        return () => task.cancel();
    }, []);
    const common = useMemo(() => ({
        Recursive: true,
        IncludeItemTypes: itemType,
        Fields: 'Overview,Genres,UserData,BackdropImageTags',
        EnableImages: true,
        EnableImageTypes: 'Backdrop,Logo,Primary',
        EnableUserData: true,
        EnableTotalRecordCount: false,
        ...(libraryId ? { ParentId: libraryId } : {}),
    }), [itemType, libraryId]);
    const results = useQueries({ queries: [
        { queryKey: ['library', itemType, libraryId, 'recent'], queryFn: () => getItems(credentials!, { ...common, Limit: 30, SortBy: 'DateCreated', SortOrder: 'Descending' }) },
        { queryKey: ['library', itemType, libraryId, 'rated'], queryFn: () => getItems(credentials!, { ...common, Limit: 30, SortBy: 'CommunityRating,SortName', SortOrder: 'Descending,Ascending' }) },
        { queryKey: ['library', itemType, libraryId, 'unwatched'], queryFn: () => getItems(credentials!, { ...common, Limit: 30, IsPlayed: false, SortBy: 'DateCreated', SortOrder: 'Descending' }) },
        { queryKey: ['library', itemType, libraryId, 'releases'], queryFn: () => getItems(credentials!, { ...common, Limit: 30, SortBy: 'PremiereDate', SortOrder: 'Descending' }) },
    ].map((query, index) => ({ ...query, enabled: Boolean(credentials) && (index === 0 || deferredRowsReady), staleTime: 300_000 })) });
    const genresResult = useQuery({
        queryKey: ['library', itemType, libraryId, 'genres'],
        queryFn: () => getGenres(credentials!, itemType, libraryId),
        enabled: Boolean(credentials) && deferredRowsReady,
        staleTime: 300_000,
    });
    const catalog = useInfiniteQuery({
        queryKey: ['library', itemType, libraryId, 'all'],
        initialPageParam: 0,
        queryFn: ({ pageParam }) => getItemsPage(credentials!, { ...common, Limit: 36, StartIndex: pageParam, SortBy: 'SortName', SortOrder: 'Ascending', EnableTotalRecordCount: true }),
        getNextPageParam: (lastPage, pages) => {
            const loaded = pages.reduce((total, page) => total + page.items.length, 0);
            return loaded < lastPage.totalCount && lastPage.items.length ? loaded : undefined;
        },
        enabled: Boolean(credentials) && deferredRowsReady,
        staleTime: 300_000,
    });
    const recent = results[0].data ?? [];
    const rated = results[1].data ?? [];
    const unwatched = results[2].data ?? [];
    const releases = results[3].data ?? [];
    const shelfRows = useMemo(() => {
        const seen = new Set<string>();
        const take = (items: JellyfinItem[]) => {
            const selected: JellyfinItem[] = [];
            for (const item of items) {
                const key = item.Id;
                if (!key || seen.has(key)) continue;
                seen.add(key);
                selected.push(item);
                if (selected.length === 24) break;
            }
            return selected;
        };
        return {
            recent: take(recent),
            rated: take(rated),
            unwatched: take(unwatched),
            releases: take(releases),
        };
    }, [rated, recent, releases, unwatched]);
    const featured = useMemo(() => recent.find((item) => item.BackdropImageTags?.length && item.Overview) ?? rated.find((item) => item.BackdropImageTags?.length && item.Overview) ?? recent[0] ?? rated[0], [rated, recent]);
    const genreItems = useMemo<BrowseGenre[]>(() => {
        const artworkPool = [...recent, ...rated, ...releases];
        const counts = new Map<string, number>();
        for (const item of artworkPool) {
            for (const name of item.Genres ?? []) {
                const key = name.trim().toLocaleLowerCase();
                if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
            }
        }
        return (genresResult.data ?? [])
            .flatMap((genre) => {
                const key = genre.Name?.trim().toLocaleLowerCase() ?? '';
                const artwork = artworkPool.find((item) => item.Genres?.some((name) => name.trim().toLocaleLowerCase() === key));
                return artwork ? [{ genre, artwork, count: counts.get(key) ?? 0 }] : [];
            })
            .sort((first, second) => second.count - first.count || (first.genre.Name ?? '').localeCompare(second.genre.Name ?? ''))
            .slice(0, television ? 18 : 14)
            .map(({ genre, artwork }) => ({ genre, artwork }));
    }, [genresResult.data, rated, recent, releases, television]);
    const allItems = catalog.data?.pages.flatMap((page) => page.items) ?? [];
    const targetWidth = television ? 220 : width >= 1600 ? 182 : width >= 900 ? 166 : 124;
    const columns = Math.max(2, Math.floor((width - pagePadding * 2) / (targetWidth + 12)));
    const gridGap = compact ? 8 : 12;
    const cardWidth = Math.floor((width - pagePadding * 2 - gridGap * (columns - 1)) / columns);

    useEffect(() => {
        heroOpacity.setValue(0);
        Animated.timing(heroOpacity, { toValue: 1, duration: 240, easing: Easing.out(Easing.cubic), useNativeDriver: true }).start();
        if (!credentials || !featured) return;
        let current = true;
        void getImageAccent(credentials, featured, featured.BackdropImageTags?.length ? 'Backdrop' : 'Primary')
            .then((accent) => { if (current) setHeroAccent(accent); })
            .catch(() => { if (current) setHeroAccent('rgb(166,35,47)'); });
        return () => { current = false; };
    }, [credentials, featured?.Id, heroOpacity]);

    if (!credentials) return null;
    const openItem = (item: JellyfinItem) => item.Id && navigation.navigate('Item', { itemId: item.Id, previewItem: item });
    const playItem = (item: JellyfinItem) => item.Id && navigation.navigate('Player', { itemId: item.Id });
    const totalCount = catalog.data?.pages[0]?.totalCount ?? allItems.length;
    const metadata = featured ? [featured.ProductionYear, featured.OfficialRating, formatRuntime(featured.RunTimeTicks), typeof featured.CommunityRating === 'number' ? `★ ${featured.CommunityRating.toFixed(1)}` : undefined].filter(Boolean) : [];

    const header = (
        <View>
            <View style={[styles.hero, { height: heroHeight }]}>
                {featured ? <Animated.Image source={imageSource(credentials, featured, featured.BackdropImageTags?.length ? 'Backdrop' : 'Primary', 1920)} resizeMode="cover" style={[StyleSheet.absoluteFillObject, styles.heroImage, { opacity: heroOpacity }]} /> : null}
                <Svg pointerEvents="none" width={width} height={heroHeight} style={StyleSheet.absoluteFillObject}>
                    <Defs>
                        <LinearGradient id="libraryVertical" x1="0" y1="0" x2="0" y2="1"><Stop offset="0" stopColor="#000" stopOpacity="0.12" /><Stop offset="0.68" stopColor="#000" stopOpacity="0.38" /><Stop offset="1" stopColor={colors.background} stopOpacity="1" /></LinearGradient>
                        <LinearGradient id="libraryHorizontal" x1="0" y1="0" x2="1" y2="0"><Stop offset="0" stopColor="#09090b" stopOpacity="0.96" /><Stop offset="0.48" stopColor="#09090b" stopOpacity="0.56" /><Stop offset="1" stopColor="#09090b" stopOpacity={compact ? '0.48' : '0.1'} /></LinearGradient>
                    </Defs>
                    <Rect width={width} height={heroHeight} fill="url(#libraryVertical)" />
                    <Rect width={width} height={heroHeight} fill="url(#libraryHorizontal)" />
                </Svg>
                <Animated.View style={[styles.heroContent, compact && styles.heroContentCompact, television && styles.heroContentTelevision, { left: pagePadding, opacity: heroOpacity }]}>
                    <View style={[styles.destinationRule, { backgroundColor: heroAccent }]} />
                    <Text style={[styles.destination, television && styles.destinationTelevision]}>{destinationName.toUpperCase()}</Text>
                    {featured ? <>
                        <Text numberOfLines={2} style={[styles.heroTitle, compact && styles.heroTitleCompact, television && styles.heroTitleTelevision]}>{featured.Name ?? noun}</Text>
                        {metadata.length ? <Text style={[styles.heroMetadata, television && styles.heroMetadataTelevision]}>{metadata.join('   ')}</Text> : null}
                        {featured.Overview ? <Text numberOfLines={compact ? 2 : 3} style={[styles.heroOverview, compact && styles.heroOverviewCompact, television && styles.heroOverviewTelevision]}>{featured.Overview}</Text> : null}
                        <View style={styles.heroActions}><BrowseAction kind="play" accent={heroAccent} onPress={() => playItem(featured)} /><BrowseAction kind="info" accent={heroAccent} onPress={() => openItem(featured)} /></View>
                    </> : <Text style={[styles.heroTitle, compact && styles.heroTitleCompact]}>{noun}</Text>}
                </Animated.View>
            </View>
            <View style={styles.shelves}>
                <MediaShelf title="Recently added" shelfKey={`${itemType}-recent`} items={shelfRows.recent} credentials={credentials} onItemPress={openItem} onItemPlay={playItem} />
                <MediaShelf title="Top rated" shelfKey={`${itemType}-rated`} items={shelfRows.rated} credentials={credentials} onItemPress={openItem} onItemPlay={playItem} />
                <GenreStrip genres={genreItems} credentials={credentials} pagePadding={pagePadding} onPress={(genre) => genre.Id && navigation.navigate('Genre', {
                    genreId: genre.Id,
                    name: genre.Name ?? 'Genre',
                    ...(libraryId ? { parentId: libraryId, itemType, libraryName: destinationName } : {}),
                })} />
                <MediaShelf title="Start something new" shelfKey={`${itemType}-unwatched`} items={shelfRows.unwatched} credentials={credentials} onItemPress={openItem} onItemPlay={playItem} />
                <MediaShelf title="Newest releases" shelfKey={`${itemType}-releases`} items={shelfRows.releases} credentials={credentials} onItemPress={openItem} onItemPlay={playItem} />
            </View>
            <View style={[styles.catalogHeading, { paddingHorizontal: pagePadding }]}>
                <Text style={[styles.sectionTitle, { fontSize: browseVisuals.shelfTitleSize, lineHeight: browseVisuals.shelfTitleLineHeight }]}>All {destinationName.toLowerCase()}</Text>
                {!catalog.isLoading ? <Text style={[styles.catalogCount, television && styles.catalogCountTelevision]}>{totalCount.toLocaleString()} titles</Text> : null}
            </View>
        </View>
    );

    return (
        <SafeAreaView style={styles.safe} edges={['top']}>
            <MatureTopNav navigation={navigation} active="Library" activeItemType={itemType} overlay solid={pageScrolled} />
            {results.some((result) => result.isLoading) && !featured ? <ActivityIndicator style={styles.loader} size="large" color={colors.brand} /> : null}
            <FlatList
                key={`${itemType}-${columns}`}
                data={allItems}
                numColumns={columns}
                keyExtractor={(item, index) => item.Id ?? `${itemType}-${index}`}
                ListHeaderComponent={header}
                ListFooterComponent={catalog.isFetchingNextPage ? <ActivityIndicator style={styles.footerLoader} color={heroAccent} /> : null}
                ListEmptyComponent={!catalog.isLoading && catalog.error ? <Text style={[styles.empty, { marginHorizontal: pagePadding }]}>{catalog.error.message}</Text> : null}
                columnWrapperStyle={columns > 1 ? [styles.gridRow, { paddingHorizontal: pagePadding, gap: gridGap }] : undefined}
                contentContainerStyle={[styles.content, bottomNavInset ? { paddingBottom: bottomNavInset } : undefined]}
                renderItem={({ item }) => <MediaCard item={item} credentials={credentials} width={cardWidth} gap={0} onPress={() => openItem(item)} onPlay={() => playItem(item)} />}
                onEndReached={catalog.hasNextPage && !catalog.isFetchingNextPage ? () => { void catalog.fetchNextPage(); } : undefined}
                onEndReachedThreshold={0.7}
                onScroll={(event) => setPageScrolled(event.nativeEvent.contentOffset.y > 80)}
                scrollEventThrottle={64}
                showsVerticalScrollIndicator={false}
                initialNumToRender={columns * 3}
                maxToRenderPerBatch={columns * 2}
                windowSize={7}
                removeClippedSubviews={false}
            />
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    safe: { flex: 1, backgroundColor: colors.background },
    content: { paddingBottom: 72 },
    hero: { position: 'relative', overflow: 'hidden', backgroundColor: colors.background },
    heroImage: { transform: [{ scale: 1.025 }] },
    heroContent: { position: 'absolute', top: 128, width: '46%', maxWidth: 660 },
    heroContentCompact: { top: 84, right: 22, width: 'auto', maxWidth: 460 },
    heroContentTelevision: { top: 164, maxWidth: 760 },
    destinationRule: { width: 34, height: 3, marginBottom: 10, borderRadius: 2 },
    destination: { color: 'rgba(255,255,255,0.68)', fontFamily: systemSansFont, fontSize: 12, lineHeight: 17, fontWeight: '800', letterSpacing: 1.8 },
    destinationTelevision: { fontSize: 16, lineHeight: 22 },
    heroTitle: { marginTop: 13, color: colors.text, fontFamily: systemSansFont, fontSize: 48, lineHeight: 51, fontWeight: '800', letterSpacing: -0.8, textShadowColor: 'rgba(0,0,0,0.74)', textShadowRadius: 12 },
    heroTitleCompact: { fontSize: 32, lineHeight: 35, letterSpacing: -0.4 },
    heroTitleTelevision: { fontSize: 64, lineHeight: 68 },
    heroMetadata: { marginTop: 15, color: 'rgba(255,255,255,0.78)', fontFamily: systemSansFont, fontSize: 14, lineHeight: 20, fontWeight: '700' },
    heroMetadataTelevision: { fontSize: 18, lineHeight: 25 },
    heroOverview: { maxWidth: 610, marginTop: 11, color: 'rgba(255,255,255,0.78)', fontFamily: systemSansFont, fontSize: 16, lineHeight: 23, textShadowColor: 'rgba(0,0,0,0.76)', textShadowRadius: 8 },
    heroOverviewCompact: { maxWidth: 420, fontSize: 14, lineHeight: 20 },
    heroOverviewTelevision: { fontSize: 19, lineHeight: 28 },
    heroActions: { marginTop: 18, flexDirection: 'row', alignItems: 'center', gap: 10 },
    heroAction: { minWidth: 104, height: 42, paddingHorizontal: 17, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, borderWidth: 2, borderRadius: 8 },
    heroActionSecondary: { borderColor: 'rgba(255,255,255,0.2)', backgroundColor: 'rgba(30,30,34,0.74)' },
    heroActionActive: { transform: [{ translateY: -2 }, { scale: 1.02 }] },
    heroActionFocused: { borderColor: colors.text },
    heroActionText: { color: colors.text, fontFamily: systemSansFont, fontSize: 14, lineHeight: 20, fontWeight: '800' },
    shelves: { marginTop: -20 },
    sectionTitle: { color: colors.text, fontFamily: systemSansFont, fontWeight: '700', letterSpacing: -0.25 },
    genreSection: { marginTop: 17, marginBottom: 26 },
    genreTile: { height: 92, overflow: 'hidden', padding: 14, justifyContent: 'flex-end', borderRadius: 10, borderWidth: 1, borderColor: 'rgba(255,255,255,0.14)', backgroundColor: 'rgba(255,255,255,0.06)' },
    genreTileActive: { borderColor: 'rgba(255,255,255,0.46)', transform: [{ translateY: -2 }, { scale: 1.015 }] },
    genreTileFocused: { borderWidth: 3, borderColor: colors.text },
    genreShade: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.48)' },
    genreAccent: { position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, backgroundColor: colors.brand },
    genreName: { color: colors.text, fontFamily: systemSansFont, fontSize: 16, lineHeight: 21, fontWeight: '800', textShadowColor: '#000', textShadowRadius: 7 },
    genreNameTelevision: { fontSize: 20, lineHeight: 27 },
    catalogHeading: { marginTop: 27, marginBottom: 17, flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' },
    catalogCount: { color: 'rgba(255,255,255,0.48)', fontFamily: systemSansFont, fontSize: 13, lineHeight: 18, fontWeight: '600' },
    catalogCountTelevision: { fontSize: 17, lineHeight: 23 },
    gridRow: { marginBottom: 22 },
    loader: { position: 'absolute', top: 230, alignSelf: 'center', zIndex: 8 },
    footerLoader: { marginVertical: 28 },
    empty: { color: 'rgba(255,255,255,0.58)', fontFamily: systemSansFont, fontSize: 15, lineHeight: 22, paddingVertical: 30 },
    pressed: { opacity: 0.82 },
});
